import { Component, ComponentInterface, JSX, Prop, State, getAssetPath, h } from '@stencil/core';
import { ProductCategory, SessionStorageKeys } from '@aglenergy/shared-library';
import { ProductCatalogueProduct, PromoDialogData } from 'app/shared/models/index';

import { StoreKeys } from 'app/shared/constants/store-keys.enum';
import { eligibilityCodeService } from 'app/shared/services/eligibility-code/eligibility-code.service';
import { productService } from 'app/shared/services/productService/product.service';
import { store } from 'app/shared/store/store';
import { tealiumAnalyticService } from 'app/shared/services/tealium/tealium-analytics.service';

@Component({
  tag: 'promo-dialog',
  styleUrl: 'promo-dialog.scss'
})
export class PromoDialog implements ComponentInterface {
  @Prop() public products: ProductCatalogueProduct[];
  @Prop() public promoDialogData: PromoDialogData;
  @State() public validationText: string;
  @State() public isInputValid: boolean | null = null;
  @State() public isEligibilityCodeValid: boolean | null = null;
  @State() public siteErrorCode: string = null;
  @State() public isSubmitClicked = false;
  @State() public isLoading = false;
  private eligibilityCodeTextBox: HTMLInputElement;
  private dialogHeading: HTMLHeadingElement;
  private closeButton: HTMLAglDsIconElement;
  private submitButton: HTMLAglDsButtonElement;
  private footerErrorElement: HTMLDivElement;
  private eligibilityCode: string;

  public componentDidLoad(): void {
    if (this.dialogHeading?.focus) {
      this.dialogHeading.focus();
    }

    this.sendAnalyticsEvent('Displayed', 'modal_display');
  }

  public async checkoutBtnClicked(): Promise<void> {
    this.checkIfInputEntered();
    if (this.isInputValid) {
      await this.validateEligibilityCode();
      this.isSubmitClicked = true;
      const productCategory = store.get('productCategory');
      if (this.isEligibilityCodeValid) {
        if (productCategory?.toLowerCase() === ProductCategory.Mobile.toLowerCase()) {
          productService.onSelectedProductForMobile(store.get(StoreKeys.selectedSKU), this.products, false);
        } else if (productCategory === ProductCategory.Nbn) {
          productService.onSelectedProductForNbn(this.products, store.get('address'), store.get(StoreKeys.selectedSKU));
        }

        this.sendAnalyticsEvent('Submitted', 'modal_submit');
      } else {
        setTimeout(() => {
          this.footerErrorElement?.focus();
        }, 200);

        this.sendAnalyticsErrorEvent(this.promoDialogData?.eligibilityCodeError, this.siteErrorCode, 'API');
      }
    } else {
      setTimeout(() => {
        this.eligibilityCodeTextBox?.focus();
      }, 200);

      this.sendAnalyticsErrorEvent(this.promoDialogData?.requiredFieldErrorMessage);
    }
    this.isLoading = false;
  }

  public async validateEligibilityCode(): Promise<void> {
    const productDetailsFromSession = JSON.parse(window.sessionStorage.getItem(SessionStorageKeys.ProductDetailsSession));
    const differentiatedPricing =
      productDetailsFromSession?.length > 0 ? productDetailsFromSession[0]?.response?.products[0]?.differentiatedPricing : null;
    const eligibilityValidationSource = differentiatedPricing?.length > 0 ? differentiatedPricing?.eligibilityValidationSource : null;
    this.isLoading = true;
    await eligibilityCodeService
      .validateEligibilityCode(store.get(StoreKeys.webId), this.eligibilityCode, eligibilityValidationSource)
      .then((results) => {
        this.isEligibilityCodeValid = results?.isValid;
        this.siteErrorCode = results?.siteError?.reasonCode || null;
        return;
      });
  }

  public checkIfInputEntered(): void {
    this.eligibilityCode = this.eligibilityCodeTextBox.value;
    this.isInputValid = !!this.eligibilityCode;
  }

  public handleModalClose(): void {
    this.sendAnalyticsEvent('Modal', 'Closed', {}, {});
    store.set(StoreKeys.isPromoModalOpen, false);
    const modalTriggerElemId = store.get(StoreKeys.promoModalTriggerElementId);
    if (modalTriggerElemId) {
      const elem = document.querySelector(`#${modalTriggerElemId}`) as HTMLDlsButtonElement;
      setTimeout(() => {
        elem?.setFocus();
      });
    }
  }

  public handleTextBoxFocus(): void {
    this.isEligibilityCodeValid = null;
  }

  public handleCloseButtonKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ' ') {
      this.handleModalClose();
      event.preventDefault();
    }
  }

  public handleModalKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.handleModalClose();
    }
  }

  private sendAnalyticsErrorEvent(description: string, code = '', type: 'API' | 'Inline' = 'Inline'): void {
    const errorData = {
      code,
      type,
      description
    };

    const data = {
      event_error: this.promoDialogData?.eligibilityCodeLabel
    };

    this.sendAnalyticsEvent('Error', 'error', data, errorData);
  }

  private sendAnalyticsEvent(
    context: string,
    eventName: string,
    data: { [key: string]: string } = {},
    errorData?: {
      code?: string;
      description?: string;
      type?: string;
    }
  ) {
    const modalName = this.promoDialogData?.header;

    const updatedContext = [`Modal][${context}][${modalName}]`];

    const updatedData = {
      ...data,
      modal_name: modalName,
      page_name: window['utag_data']?.page_name || 'unknown',
      page_URL: window['utag_data']?.page_URL || 'unknown'
    };

    const productCategory = store.get('productCategory');

    tealiumAnalyticService.sendEvent(updatedContext, null, eventName, updatedData, productCategory, errorData);
  }

  public render(): JSX.Element {
    return (
      <div class="promo-dialog-overlay">
        <div
          class="promo-dialog"
          role="dialog"
          aria-modal="true"
          aria-labelledby="promoDialogTitle"
          tabIndex={-1}
          onKeyDown={(event) => this.handleModalKeyDown(event)}
        >
          <div
            class="promo-dialog__focus-trap"
            tabIndex={0}
            onFocus={() =>
              setTimeout(() => {
                this.submitButton?.setFocus();
              }, 100)
            }
          />{' '}
          {/* trap the focus so it doesn't go below the modal */}
          <div class="promo-dialog__header" tabIndex={-1}>
            <h1 id="promoDialogTitle" tabIndex={-1} ref={(el: HTMLHeadingElement) => (this.dialogHeading = el)}>
              {this.promoDialogData?.header}
            </h1>
            <agl-ds-icon
              class="promo-dialog-icon"
              aria-label="close"
              tabIndex={0}
              size="sm"
              iconPath={getAssetPath('../assets/icon/agl_icon_cross.svg')}
              onClick={() => this.handleModalClose()}
              onKeyDown={(event) => this.handleCloseButtonKeyDown(event)}
              ref={(el: HTMLAglDsIconElement) => (this.closeButton = el)}
            />
          </div>
          <div class="promo-dialog__content">
            <div class="promo-dialog__content-container">
              <div class="promo-dialog__content-header">{this.promoDialogData?.contentHeader}</div>
              <agl-ds-spacer orientation="vertical" size="space02" />
              <agl-ds-trusted-html-content containerBorder="none" containerSize="auto" innerHTML={this.promoDialogData?.content} />
            </div>
          </div>
          <div class="promo-dialog__body">
            <label class="promo-dialog__body-label" htmlFor="eligibilityCodeInput">
              {this.promoDialogData?.eligibilityCodeLabel}
            </label>
            <input
              id="eligibilityCodeInput"
              type="text"
              class={{ 'promo-dialog__eligibility-code-input': true, 'promo-dialog__input--error': this.isInputValid === false }}
              placeholder={this.promoDialogData?.eligibilityCodePlaceholder}
              aria-describedby="eligibilityCodeInputError eligibilityCodeInputDescription"
              ref={(el: HTMLInputElement) => (this.eligibilityCodeTextBox = el)}
              onBlur={() => this.checkIfInputEntered()}
              onFocus={() => this.handleTextBoxFocus()}
              onPaste={() =>
                setTimeout(() => {
                  this.checkIfInputEntered();
                })
              }
              onKeyUp={() =>
                setTimeout(() => {
                  this.checkIfInputEntered();
                })
              }
            />
            {this.promoDialogData?.eligibilityCodeHintText && (
              <div id="eligibilityCodeInputDescription" class="promo-dialog__hint-text">
                {this.promoDialogData?.eligibilityCodeHintText}
              </div>
            )}

            {this.isInputValid === false && (
              <div class="promo-dialog__body-error">
                <agl-ds-icon
                  class="promo-dialog__body-error-icon promo-dialog__error-icon"
                  aria-hidden="true"
                  size="xs"
                  iconPath={getAssetPath('../assets/icon/info.svg')}
                />
                <div id="eligibilityCodeInputError" class="promo-dialog__body-error-text">
                  {this.promoDialogData?.requiredFieldErrorMessage}
                </div>
              </div>
            )}
          </div>
          <div class="promo-dialog__footer">
            {this.isInputValid && this.isSubmitClicked && this.isEligibilityCodeValid == false && (
              <div class="promo-dialog__footer-error" tabIndex={-1} ref={(el: HTMLDivElement) => (this.footerErrorElement = el)}>
                <agl-ds-icon
                  class="promo-dialog__footer-error-icon promo-dialog__error-icon"
                  aria-hidden="true"
                  size="xs"
                  iconPath={getAssetPath('../assets/icon/info.svg')}
                />
                <p class="promo-dialog__footer-error-text">{this.promoDialogData?.eligibilityCodeError}</p>
              </div>
            )}

            <agl-ds-button
              class="promo-dialog__footer-submit-button"
              type="primary"
              loading={this.isLoading}
              ref={(el: HTMLAglDsButtonElement) => (this.submitButton = el)}
              onClick={() => this.checkoutBtnClicked()}
            >
              {this.promoDialogData?.submitButtonLabel}
            </agl-ds-button>
          </div>
          <div
            class="promo-dialog__focus-trap"
            tabIndex={0}
            onFocus={() =>
              setTimeout(() => {
                this.closeButton?.focus();
              }, 100)
            }
          />{' '}
          {/* trap the focus so it doesn't go below the modal */}
        </div>
      </div>
    );
  }
}
